import {
	useDashboard,
	usePublicIntegrityAlerts,
	usePublicSystemHealth,
} from "@homesite/hooks";
import type { HealthSummaryCardProps, TamperAlertItem } from "@homesite/ui";
import { DashboardPage } from "@homesite/ui";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { rootRoute } from "./__root";

const EVENT_HASH_RE = /^0x[a-fA-F0-9]{64}$/;

/** Anchoring is slow when batches are backing up or the oldest one is stale. */
function hasAnchoringLag(
	pendingBatches: number,
	oldestPendingBatchAge: number | null,
): boolean {
	if (oldestPendingBatchAge != null && oldestPendingBatchAge > 3_600)
		return true;
	return pendingBatches > 5;
}

/**
 * The primary verdict reflects integrity only. Anchoring latency never turns
 * the trust verdict amber — it is surfaced as neutral subtext instead.
 */
function deriveHealthStatus(
	alertCount: number,
): HealthSummaryCardProps["status"] {
	return alertCount > 0 ? "tampered" : "healthy";
}

const STATUS_HEADLINE: Record<HealthSummaryCardProps["status"], string> = {
	healthy: "No tampering detected. All records verified.",
	degraded: "No tampering detected. All records verified.",
	tampered: "Tampering detected — review the alerts below.",
};

const STATUS_SUBTEXT: Record<HealthSummaryCardProps["status"], string> = {
	healthy: "Every record matches the official ledger.",
	degraded: "Every record matches the official ledger.",
	tampered:
		"One or more integrity alerts have been raised. Review the alerts below.",
};

export const dashboardRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: function Dashboard() {
		const navigate = useNavigate();
		const { recentBatches, loading: dashLoading, error } = useDashboard();
		const { health: systemHealth, isLoading: healthLoading } =
			usePublicSystemHealth();
		const { alerts: tamperAlerts, isLoading: alertsLoading } =
			usePublicIntegrityAlerts();

		const [searchValue, setSearchValue] = useState("");

		const loading = dashLoading || healthLoading || alertsLoading;

		function handleSearchSubmit(): void {
			const trimmed = searchValue.trim();
			if (!trimmed) return;
			if (EVENT_HASH_RE.test(trimmed)) {
				navigate({ to: "/events/$hash", params: { hash: trimmed } });
			} else {
				navigate({ to: "/loans/$loanId", params: { loanId: trimmed } });
			}
		}

		const status = deriveHealthStatus(tamperAlerts.length);

		const anchoringLag = hasAnchoringLag(
			systemHealth?.pendingBatches ?? 0,
			systemHealth?.oldestPendingBatchAge ?? null,
		);

		const subtext =
			status === "healthy" && anchoringLag
				? "All records verified. Some recent records are still being confirmed."
				: STATUS_SUBTEXT[status];

		const health: HealthSummaryCardProps = {
			status,
			headline: STATUS_HEADLINE[status],
			subtext,
			lastAnchorAt: systemHealth?.lastBatchAnchoredAt ?? null,
			eventsLast24h: systemHealth?.eventVolumeLast30d.slice(-1)[0]?.count ?? 0,
		};

		const recentActivity = recentBatches.slice(0, 5).map((b) => ({
			id: b.merkleRoot,
			type: b.anchored
				? ("batch_anchored" as const)
				: ("batch_created" as const),
			timestamp: b.createdAt,
			summary: `Batch ${b.merkleRoot.slice(0, 10)}… (${b.recordCount} records)`,
			link: `/batches/${b.merkleRoot}`,
		}));

		const mappedAlerts: TamperAlertItem[] = tamperAlerts.map((a) => ({
			id: a.id,
			alertType: a.alertType,
			severity: a.severity as TamperAlertItem["severity"],
			createdAt: a.createdAt,
			batchRoot: a.batchRoot,
			chainId: a.chainId,
			loanCaseIdHash: a.loanCaseIdHash,
			eventType: a.eventType,
		}));

		function handleSelectAlert(alert: TamperAlertItem): void {
			if (alert.batchRoot) {
				navigate({
					to: "/batches/$root",
					params: { root: alert.batchRoot },
				});
			}
		}

		return (
			<DashboardPage
				health={health}
				tamperAlerts={mappedAlerts}
				onSelectAlert={handleSelectAlert}
				recentActivity={recentActivity}
				loading={loading}
				error={error}
				searchValue={searchValue}
				onSearchChange={setSearchValue}
				onSearchSubmit={handleSearchSubmit}
				onVerifyLoan={() => navigate({ to: "/loans" })}
				onVerifyEvent={() => navigate({ to: "/events" })}
				onGetCertificate={() => navigate({ to: "/reports" })}
			/>
		);
	},
});
